import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { getData, postData, putData, deleteData } from "../../../FetchApiServices";
import "../T2T.css";

// ── Permission flags (same as T2TAdmin) ──
const PERMISSION_GROUPS = [
  { label: "Workflow Builder", flags: [
    { key: "workflow_builder.view", label: "View" }, { key: "workflow_builder.create", label: "Create" },
    { key: "workflow_builder.edit", label: "Edit" }, { key: "workflow_builder.publish", label: "Publish" },
    { key: "workflow_builder.archive", label: "Archive" }, { key: "workflow_builder.delete", label: "Delete" },
    { key: "workflow_builder.clone", label: "Clone" },
  ]},
  { label: "Administration", flags: [
    { key: "org.manage", label: "Manage Org" }, { key: "roles.manage", label: "Manage Roles" }, { key: "users.manage", label: "Manage Users" },
  ]},
  { label: "Requests", flags: [{ key: "requests.view_all", label: "View All" }, { key: "requests.reassign", label: "Reassign" }] },
  { label: "Reports", flags: [{ key: "reports.view", label: "View" }, { key: "reports.export", label: "Export" }] },
];

const STARTER_ROLES = [
  { name: "Workflow Designer", perms: { "workflow_builder.view": true, "workflow_builder.create": true, "workflow_builder.edit": true, "workflow_builder.clone": true, "reports.view": true } },
  { name: "Workflow Publisher", perms: { "workflow_builder.view": true, "workflow_builder.create": true, "workflow_builder.edit": true, "workflow_builder.publish": true, "workflow_builder.archive": true, "workflow_builder.delete": true, "workflow_builder.clone": true } },
  { name: "Department Manager", perms: { "requests.view_all": true, "requests.reassign": true, "reports.view": true, "reports.export": true } },
  { name: "End User", perms: {} },
];

const COLORS = ["#36B82A","#2563EB","#D97706","#DC2626","#7C3AED","#0F766E","#1B3A6B","#DB2777"];

const T2TOnboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress]       = useState({ step_1_complete: false, step_2_complete: false, step_3_complete: false, step_4_complete: false });
  const [loading, setLoading]         = useState(true);

  // Fetch saved progress on mount
  useEffect(() => {
    const fetch = async () => {
      const resp = await getData("t2t/onboarding/progress");
      if (!resp.error && resp.data?.data) {
        const p = resp.data.data;
        setProgress(p);
        setCurrentStep(p.current_step || 1);
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const saveProgress = async (step, complete) => {
    const updated = { ...progress, [`step_${step}_complete`]: complete, current_step: step };
    setProgress(updated);
    await putData("t2t/onboarding/progress", updated);
  };

  const nextStep = () => {
    if (currentStep < 4) {
      saveProgress(currentStep, true);
      setCurrentStep(currentStep + 1);
    }
  };

  const goToStep = (step) => {
    if (step <= currentStep || progress[`step_${step - 1}_complete`] || step === 1) {
      setCurrentStep(step);
    }
  };

  if (loading) {
    return (
      <div className="t2t-module" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 13, color: "var(--t2t-text-muted)" }}>Loading setup wizard...</div>
      </div>
    );
  }

  const STEPS = [
    { num: 1, title: "Organisation Setup" },
    { num: 2, title: "Roles & Permissions" },
    { num: 3, title: "Create Users" },
    { num: 4, title: "Review & Go Live" },
  ];

  return (
    <div className="t2t-module" style={{ minHeight: "100vh", background: "var(--t2t-bg)" }}>
      {/* Header */}
      <div style={{ background: "#1a2433", padding: "16px 32px", display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ color: "#36B82A", fontWeight: 800, fontSize: 18 }}>T2T</div>
        <div style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>Setup Wizard</div>
      </div>

      {/* Progress Stepper */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, padding: "24px 32px 16px", background: "#fff", borderBottom: "1px solid var(--t2t-border)" }}>
        {STEPS.map((s, i) => {
          const done = progress[`step_${s.num}_complete`];
          const active = currentStep === s.num;
          return (
            <React.Fragment key={s.num}>
              {i > 0 && <div style={{ width: 60, height: 2, background: done ? "#36B82A" : "#E2E8F0", margin: "0 8px" }} />}
              <div
                style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", opacity: active ? 1 : 0.7 }}
                onClick={() => goToStep(s.num)}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 700,
                  background: done ? "#36B82A" : active ? "#1B3A6B" : "#E2E8F0",
                  color: done || active ? "#fff" : "#64748B",
                }}>
                  {done ? "✓" : s.num}
                </div>
                <span style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? "#1B3A6B" : "#64748B" }}>{s.title}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 16px" }}>
        {currentStep === 1 && <Step1OrgSetup onNext={nextStep} />}
        {currentStep === 2 && <Step2Roles onNext={nextStep} />}
        {currentStep === 3 && <Step3Users onNext={nextStep} />}
        {currentStep === 4 && <Step4Review navigate={navigate} />}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// STEP 1 — Org Setup (simplified tree)
// ═══════════════════════════════════════════════════════════════════
const Step1OrgSetup = ({ onNext }) => {
  const [orgUnits, setOrgUnits] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [name, setName]         = useState("");
  const [type, setType]         = useState("Company");
  const [parentId, setParentId] = useState(null);

  useEffect(() => { fetchUnits(); }, []);
  const fetchUnits = async () => {
    setLoading(true);
    const resp = await getData("t2t/org-units");
    if (!resp.error) {
      const raw = resp.data?.data;
      setOrgUnits(Array.isArray(raw) ? raw : []);
    }
    setLoading(false);
  };

  const addUnit = async () => {
    if (!name.trim()) return;
    const resp = await postData("t2t/org-units", { name: name.trim(), type, parent_id: parentId });
    if (!resp.error) { setName(""); fetchUnits(); }
  };

  const removeUnit = async (id) => {
    await deleteData(`t2t/org-units/${id}`);
    fetchUnits();
  };

  const roots = orgUnits.filter((o) => !o.parent_id);
  const children = (pid) => orgUnits.filter((o) => String(o.parent_id) === String(pid));

  const renderTree = (nodes, depth = 0) => nodes.map((n) => (
    <div key={n.id} style={{ marginLeft: depth * 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 6, marginBottom: 4, background: depth === 0 ? "#F0FDF4" : "#F8FAFC", border: "1px solid #E2E8F0" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#64748B", background: "#E2E8F0", padding: "1px 6px", borderRadius: 4 }}>{n.type}</span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{n.name}</span>
        <button onClick={() => { setParentId(n.id); setType(depth === 0 ? "Division" : "Department"); }} style={{ fontSize: 10, color: "var(--t2t-primary)", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>+ Child</button>
        <button onClick={() => removeUnit(n.id)} style={{ fontSize: 12, color: "#DC2626", background: "none", border: "none", cursor: "pointer" }}>✕</button>
      </div>
      {renderTree(children(n.id), depth + 1)}
    </div>
  ));

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Organisation Setup</h2>
      <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 20 }}>Build your company structure. Start with your top-level entity (company or group).</p>

      {loading ? <div style={{ padding: 20, textAlign: "center", color: "var(--t2t-text-muted)" }}>Loading...</div> : (
        <>
          {roots.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "var(--t2t-text-muted)", fontSize: 13 }}>No org units yet. Add your first one below.</div>}
          {renderTree(roots)}
        </>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 16, alignItems: "flex-end" }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B" }}>Name</label>
          <input className="t2t-fc" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jindal Steel" />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B" }}>Type</label>
          <select className="t2t-fc" value={type} onChange={(e) => setType(e.target.value)}>
            {["Group", "Company", "Division", "Department", "Plant", "Section", "Team"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B" }}>Parent</label>
          <select className="t2t-fc" value={parentId || ""} onChange={(e) => setParentId(e.target.value || null)}>
            <option value="">None (root)</option>
            {orgUnits.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.type})</option>)}
          </select>
        </div>
        <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={addUnit} style={{ height: 38 }}>+ Add</button>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
        <button className="t2t-btn t2t-btn-ghost" onClick={onNext}>Skip for now</button>
        <button className="t2t-btn t2t-btn-primary" onClick={onNext} disabled={orgUnits.length === 0}>Next Step →</button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// STEP 2 — Roles & Permissions
// ═══════════════════════════════════════════════════════════════════
const Step2Roles = ({ onNext }) => {
  const [roles, setRoles]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ name: "", description: "", color: COLORS[0], permissions: {} });
  const [saving, setSaving]     = useState(false);

  useEffect(() => { fetchRoles(); }, []);
  const fetchRoles = async () => {
    setLoading(true);
    const resp = await getData("t2t/roles");
    if (!resp.error) {
      const raw = resp.data?.data;
      setRoles(Array.isArray(raw) ? raw : []);
    }
    setLoading(false);
  };

  const applyTemplate = (tpl) => {
    setForm({ name: tpl.name, description: "", color: COLORS[Math.floor(Math.random() * COLORS.length)], permissions: { ...tpl.perms } });
    setShowForm(true);
  };

  const saveRole = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const resp = await postData("t2t/roles", { name: form.name, description: form.description, color: form.color, permissions: form.permissions });
    if (!resp.error) { setShowForm(false); setForm({ name: "", description: "", color: COLORS[0], permissions: {} }); fetchRoles(); }
    setSaving(false);
  };

  const deleteRole = async (id) => {
    await deleteData(`t2t/roles/${id}`);
    fetchRoles();
  };

  const togglePerm = (key) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Roles & Permissions</h2>
      <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 16 }}>Create roles and set what each role can do. Start from a template or build your own.</p>

      {/* Starter templates */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#64748B", lineHeight: "28px" }}>Quick start:</span>
        {STARTER_ROLES.map((tpl) => (
          <button key={tpl.name} className="t2t-btn t2t-btn-outline t2t-btn-sm" style={{ fontSize: 11 }} onClick={() => applyTemplate(tpl)}>{tpl.name}</button>
        ))}
      </div>

      {loading ? <div style={{ padding: 20, textAlign: "center", color: "var(--t2t-text-muted)" }}>Loading...</div> : (
        <>
          {roles.map((r) => (
            <div key={r.role_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 8, marginBottom: 8 }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: r.color || "#94A3B8" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{r.name}</div>
                {r.description && <div style={{ fontSize: 11, color: "#64748B" }}>{r.description}</div>}
              </div>
              {r.name !== "Super Admin" && <button onClick={() => deleteRole(r.role_id)} style={{ background: "none", border: "none", color: "#DC2626", cursor: "pointer", fontSize: 14 }}>✕</button>}
            </div>
          ))}
        </>
      )}

      {/* Add role form */}
      {showForm ? (
        <div style={{ background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 8, padding: 16, marginTop: 12 }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <div className="t2t-fg" style={{ flex: 2 }}><label>Role Name</label><input className="t2t-fc" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Finance Controller" /></div>
            <div className="t2t-fg" style={{ flex: 2 }}><label>Description</label><input className="t2t-fc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description" /></div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, display: "block" }}>Permissions</label>
            {PERMISSION_GROUPS.map((g) => (
              <div key={g.label} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4 }}>{g.label}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px" }}>
                  {g.flags.map((f) => (
                    <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                      <input type="checkbox" checked={!!form.permissions[f.key]} onChange={() => togglePerm(f.key)} /> {f.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={saveRole} disabled={saving}>{saving ? "Saving..." : "Save Role"}</button>
          </div>
        </div>
      ) : (
        <button className="t2t-btn t2t-btn-outline t2t-btn-sm" style={{ marginTop: 8 }} onClick={() => setShowForm(true)}>+ Add Custom Role</button>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
        <button className="t2t-btn t2t-btn-ghost" onClick={onNext}>Skip for now</button>
        <button className="t2t-btn t2t-btn-primary" onClick={onNext} disabled={roles.length === 0}>Next Step →</button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// STEP 3 — User Creation
// ═══════════════════════════════════════════════════════════════════
const Step3Users = ({ onNext }) => {
  const [users, setUsers]       = useState([]);
  const [roles, setRoles]       = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ name: "", email: "", password: "", t2t_role_id: "", group_id: "", company_id: "", job_title: "" });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkParsed, setBulkParsed] = useState([]);
  const [bulkImporting, setBulkImporting] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const [uResp, rResp, oResp] = await Promise.all([
        getData("users?is_t2t=true"),
        getData("t2t/roles"),
        getData("t2t/org-units"),
      ]);
      if (!uResp.error) setUsers(uResp.data?.data || []);
      if (!rResp.error) setRoles(Array.isArray(rResp.data?.data) ? rResp.data.data : []);
      if (!oResp.error) setOrgUnits(Array.isArray(oResp.data?.data) ? oResp.data.data : []);
      setLoading(false);
    };
    fetch();
  }, []);

  // Generate random 12-char password
  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*";
    let pw = "";
    for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setForm((f) => ({ ...f, password: pw }));
  };

  const saveUser = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) { setError("Name, email and password are required."); return; }
    setError("");
    setSaving(true);
    const payload = {
      name: form.name, email: form.email, password: form.password, is_t2t: true, t2t_job_title: form.job_title,
    };
    if (form.t2t_role_id) payload.t2t_role_id = Number(form.t2t_role_id);
    if (form.group_id) payload.t2t_group_id = Number(form.group_id);
    if (form.company_id) payload.t2t_company_id = Number(form.company_id);

    const resp = await postData("users", payload);
    if (resp.error) { setError(resp.error.message || "Failed to create user."); setSaving(false); return; }
    setShowForm(false);
    setForm({ name: "", email: "", password: "", t2t_role_id: "", group_id: "", company_id: "", job_title: "" });
    setSaving(false);
    // Refresh user list
    const uResp = await getData("users?is_t2t=true");
    if (!uResp.error) setUsers(uResp.data?.data || []);
  };

  const copyCredentials = (user) => {
    const text = `Your T2T login:\nEmail: ${user.email}\nPassword: (set by admin)\nURL: ${window.location.origin}\nChange your password on first login.`;
    navigator.clipboard.writeText(text);
  };

  // Bulk import — parse pasted tab/comma-separated text (Name, Email, Role)
  const parseBulkText = (text) => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    const parsed = lines.map((line) => {
      const cols = line.includes("\t") ? line.split("\t") : line.split(",");
      const name = (cols[0] || "").trim();
      const email = (cols[1] || "").trim();
      const roleName = (cols[2] || "").trim();
      const matchedRole = roles.find((r) => r.name.toLowerCase() === roleName.toLowerCase());
      return { name, email, roleId: matchedRole ? matchedRole.role_id : "", roleName, valid: !!name && !!email && /\S+@\S+\.\S+/.test(email) };
    });
    setBulkParsed(parsed);
  };

  const importBulkUsers = async () => {
    const valid = bulkParsed.filter((r) => r.valid);
    if (valid.length === 0) return;
    setBulkImporting(true);
    let created = 0;
    for (const row of valid) {
      const pw = Array.from({ length: 12 }, () => "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*"[Math.floor(Math.random() * 62)]).join("");
      const payload = { name: row.name, email: row.email, password: pw, is_t2t: true };
      if (row.roleId) payload.t2t_role_id = Number(row.roleId);
      const resp = await postData("users", payload);
      if (!resp.error) created++;
    }
    setBulkImporting(false);
    setShowBulk(false);
    setBulkText("");
    setBulkParsed([]);
    // Refresh user list
    const uResp = await getData("users?is_t2t=true");
    if (!uResp.error) setUsers(uResp.data?.data || []);
    setError(created > 0 ? "" : "No users were created. Check for duplicate emails.");
  };

  const groups = orgUnits.filter((o) => o.type === "Group");
  const companies = orgUnits.filter((o) => o.type === "Company" && String(o.parent_id) === String(form.group_id));

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Create Users</h2>
      <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 16 }}>Add user accounts. Each user will need to change their password on first login.</p>

      {loading ? <div style={{ padding: 20, textAlign: "center", color: "var(--t2t-text-muted)" }}>Loading...</div> : (
        <>
          {users.map((u) => (
            <div key={u.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 8, marginBottom: 8 }}>
              <div className="t2t-avatar" style={{ width: 32, height: 32, fontSize: 11 }}>
                {(u.name || "").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{u.name}</div>
                <div style={{ fontSize: 11, color: "#64748B" }}>{u.email}</div>
              </div>
              {u.t2t_role_name && <span style={{ fontSize: 10, fontWeight: 700, color: u.t2t_role_color || "#64748B", background: (u.t2t_role_color || "#94A3B8") + "18", padding: "2px 8px", borderRadius: 10 }}>{u.t2t_role_name}</span>}
              <button onClick={() => copyCredentials(u)} style={{ fontSize: 10, color: "var(--t2t-primary)", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>📋 Copy</button>
            </div>
          ))}
        </>
      )}

      {showForm ? (
        <div style={{ background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 8, padding: 16, marginTop: 12 }}>
          {error && <div style={{ padding: "8px 12px", background: "#FEF2F2", color: "#DC2626", borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{error}</div>}
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <div className="t2t-fg" style={{ flex: 1 }}><label>Full Name *</label><input className="t2t-fc" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ravi Kumar" /></div>
            <div className="t2t-fg" style={{ flex: 1 }}><label>Email *</label><input className="t2t-fc" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ravi@company.com" /></div>
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <div className="t2t-fg" style={{ flex: 1 }}>
              <label>Password *</label>
              <div style={{ display: "flex", gap: 6 }}>
                <input className="t2t-fc" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 chars" style={{ flex: 1 }} />
                <button className="t2t-btn t2t-btn-outline t2t-btn-sm" style={{ fontSize: 10, whiteSpace: "nowrap" }} onClick={generatePassword}>Generate</button>
              </div>
            </div>
            <div className="t2t-fg" style={{ flex: 1 }}><label>Role</label>
              <select className="t2t-fc" value={form.t2t_role_id} onChange={(e) => setForm({ ...form, t2t_role_id: e.target.value })}>
                <option value="">Select role...</option>
                {roles.map((r) => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <div className="t2t-fg" style={{ flex: 1 }}><label>Group</label>
              <select className="t2t-fc" value={form.group_id} onChange={(e) => setForm({ ...form, group_id: e.target.value, company_id: "" })}>
                <option value="">Select...</option>
                {groups.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="t2t-fg" style={{ flex: 1 }}><label>Company</label>
              <select className="t2t-fc" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} disabled={!form.group_id}>
                <option value="">Select...</option>
                {companies.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
            <div className="t2t-fg" style={{ flex: 1 }}><label>Job Title</label><input className="t2t-fc" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Analyst" /></div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={saveUser} disabled={saving}>{saving ? "Creating..." : "Create User"}</button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => { setShowForm(true); generatePassword(); }}>+ Add User</button>
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => setShowBulk(true)}>Bulk Add</button>
        </div>
      )}

      {/* Bulk Import Panel */}
      {showBulk && (
        <div style={{ background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 8, padding: 16, marginTop: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Bulk Add Users</div>
          <p style={{ fontSize: 11, color: "#64748B", marginBottom: 10 }}>
            Paste rows from Excel — each row: <strong>Name, Email, Role</strong> (tab or comma separated). Passwords are auto-generated.
          </p>
          <textarea
            className="t2t-fc"
            rows="5"
            placeholder={"Ravi Kumar\travi@company.com\tEnd User\nPreeti Sharma\tpreeti@company.com\tDepartment Manager"}
            value={bulkText}
            onChange={(e) => { setBulkText(e.target.value); parseBulkText(e.target.value); }}
          />
          {bulkParsed.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Preview ({bulkParsed.filter((r) => r.valid).length} valid of {bulkParsed.length}):</div>
              {bulkParsed.map((r, i) => (
                <div key={i} style={{ padding: "4px 8px", fontSize: 11, background: r.valid ? "#F0FDF4" : "#FEF2F2", borderRadius: 4, marginBottom: 2, display: "flex", gap: 8 }}>
                  <span>{r.valid ? "✓" : "✕"}</span>
                  <span style={{ flex: 1 }}>{r.name || "(no name)"}</span>
                  <span style={{ flex: 1 }}>{r.email || "(no email)"}</span>
                  <span style={{ color: r.roleId ? "#16A34A" : "#94A3B8" }}>{r.roleName || "(no role)"}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => { setShowBulk(false); setBulkText(""); setBulkParsed([]); }}>Cancel</button>
            <button className="t2t-btn t2t-btn-primary t2t-btn-sm" onClick={importBulkUsers} disabled={bulkImporting || bulkParsed.filter((r) => r.valid).length === 0}>
              {bulkImporting ? "Importing..." : `Import ${bulkParsed.filter((r) => r.valid).length} Users`}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
        <button className="t2t-btn t2t-btn-ghost" onClick={onNext}>Skip for now</button>
        <button className="t2t-btn t2t-btn-primary" onClick={onNext}>Next Step →</button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// STEP 4 — Review & Go Live
// ═══════════════════════════════════════════════════════════════════
const Step4Review = ({ navigate }) => {
  const [summary, setSummary] = useState({ orgUnits: 0, roles: 0, users: 0 });
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const [oResp, rResp, uResp] = await Promise.all([
        getData("t2t/org-units"),
        getData("t2t/roles"),
        getData("users?is_t2t=true"),
      ]);
      setSummary({
        orgUnits: Array.isArray(oResp.data?.data) ? oResp.data.data.length : 0,
        roles:    Array.isArray(rResp.data?.data) ? rResp.data.data.length : 0,
        users:    Array.isArray(uResp.data?.data) ? uResp.data.data.length : 0,
      });
    };
    fetch();
  }, []);

  const goLive = async () => {
    setCompleting(true);
    await postData("t2t/onboarding/complete", {});
    setCompleting(false);
    navigate("/t2t-home");
  };

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>Review & Go Live</h2>
      <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 20 }}>Review your setup and activate the system.</p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        <div style={{ background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 10, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: "#36B82A" }}>{summary.orgUnits}</div>
          <div style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Org Units</div>
        </div>
        <div style={{ background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 10, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: "#2563EB" }}>{summary.roles}</div>
          <div style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Roles</div>
        </div>
        <div style={{ background: "#fff", border: "1px solid var(--t2t-border)", borderRadius: 10, padding: 20, textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: "#7C3AED" }}>{summary.users}</div>
          <div style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Users</div>
        </div>
      </div>

      <div style={{ padding: "14px 18px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, fontSize: 12, color: "#1E40AF", marginBottom: 24, lineHeight: 1.6 }}>
        <strong>Next steps after Go Live:</strong>
        <br />1. Build your first workflow in the Workflow Builder
        <br />2. Publish the workflow to make it available
        <br />3. Users can then submit requests through the published workflow
        <br />You can always come back to Settings to add more org units, roles, or users.
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          className="t2t-btn t2t-btn-primary t2t-btn-lg"
          style={{ fontSize: 16, padding: "12px 48px" }}
          onClick={goLive}
          disabled={completing}
        >
          {completing ? "Activating..." : "✓ Complete Setup & Go Live"}
        </button>
      </div>
    </div>
  );
};

Step1OrgSetup.propTypes = { onNext: PropTypes.func.isRequired };
Step2Roles.propTypes    = { onNext: PropTypes.func.isRequired };
Step3Users.propTypes    = { onNext: PropTypes.func.isRequired };
Step4Review.propTypes   = { navigate: PropTypes.func.isRequired };

export default T2TOnboarding;
